import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useQuery } from "react-query";
import { Plan } from "@ecologi/react-components/src/Plan";
import { planTypes } from "@ecologi/react-components/src/Plan/components/PlanSelectButtons";
import { useDebounce } from "@ecologi/react-components/src/utils/use-debounce";
import { useStore } from "../../../store";
import { useCreateSubscription, useUpdateSubscription } from "../hooks";
import { selectErrorMessageFromResult } from "../selectors";
import { Head } from "../../../components";
import {
  CampaignSourceEnum,
  hasQueryInitiated,
  PlatformFeature,
} from "@ecologi/shared";
import { getSubscriptionCouponCode } from "../../../lib";
import { onSubmitCheckoutForm } from "@ecologi/react-components/src/CheckoutForm/utils/on-submit-checkout-form";
import { useGetConfigCatValue } from "../../../lib/configcat";

export function ConsumerPlansPage() {
  const isProduction = process.env.NEXT_PUBLIC_DEPLOYMENT === "production";

  const { user, location, stripe } = useStore();
  const { isReady: isRouterReady, query } = useRouter();

  const [selectedPlanType, setSelectedPlanType] = useState(planTypes.personal);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [campaignSource, setCampaignSource] = useState<CampaignSourceEnum>();
  const [couponCode, setCouponCode] = useState<string | undefined>();

  const [createSubscriptionMutation, createSubscriptionResult] =
    useCreateSubscription();

  const [updateSubscriptionMutation, updateSubscriptionResult] =
    useUpdateSubscription();

  const debouncedCouponCode = useDebounce(couponCode, 500);
  const hasCreationInitiated = hasQueryInitiated(createSubscriptionResult);

  // Passing 'isLoading' as true even when the create query has succeeded ensures the
  // form doesn't unlock in the brief moment between the mutation succeeding and the user
  // being sent to the thanks page
  const isLoading =
    createSubscriptionResult.isLoading ||
    createSubscriptionResult.isSuccess ||
    updateSubscriptionResult.isLoading;

  const isUpdateSubscription = Boolean(
    user && user.subscription && !hasCreationInitiated
  );

  const couponCodeResult = useQuery(
    ["subscription-coupon-code", debouncedCouponCode],
    () => getSubscriptionCouponCode(debouncedCouponCode, selectedPlanId),
    {
      enabled: !!debouncedCouponCode,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const [isCouponRedemptionEnabled, isCheckingCouponRedemption] =
    useGetConfigCatValue(PlatformFeature.CouponRedemption);

  const checkoutErrorMessage = useMemo(
    () => selectErrorMessageFromResult(createSubscriptionResult),
    [createSubscriptionResult]
  );

  useEffect(() => {
    if (isValidPlanType(query.planType)) {
      setSelectedPlanType(query.planType);
    }

    if (isValidCampaignSource(query.campaignSource)) {
      setCampaignSource(query.campaignSource);
    }
  }, [isRouterReady]);

  return (
    <>
      <Head title="Your plan" />
      <Plan
        user={user}
        location={location}
        updateSubscription={updateSubscriptionMutation}
        isLoading={isLoading}
        isUpdateSubscription={isUpdateSubscription}
        selectedPlanType={selectedPlanType}
        onSelectedPlanTypeChange={setSelectedPlanType}
        selectedPlanId={selectedPlanId}
        onSelectedPlanIdChange={setSelectedPlanId}
        renderCheckoutForm={(props) => (
          <Plan.CheckoutForm
            {...props}
            campaignSource={campaignSource}
            couponCodeResult={couponCodeResult}
            errorMessage={checkoutErrorMessage}
            isCouponRedemptionEnabled={isCouponRedemptionEnabled}
            isLoading={isLoading || isCheckingCouponRedemption}
            isProduction={isProduction}
            location={location}
            onCouponCodeChange={setCouponCode}
            onSubmit={onSubmitCheckoutForm}
            stripe={stripe}
            subscribeUser={createSubscriptionMutation}
          />
        )}
      />
    </>
  );
}

const isValidPlanType = (planType: any): planType is string => {
  return (
    typeof planType === "string" && Object.values(planTypes).includes(planType)
  );
};

const isValidCampaignSource = (
  campaignSource: any
): campaignSource is CampaignSourceEnum => {
  return (
    typeof campaignSource === "string" &&
    Object.values(CampaignSourceEnum).includes(
      campaignSource as CampaignSourceEnum
    )
  );
};
